import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import type { Plan } from '../_shared/types';
import { plansKey } from '../_shared/queries';

/**
 * The three writes this surface owns.
 *
 * **No mutation auto-retries**, and none is optimistic: a plan is a money
 * record, and a row that renders as saved before the server agreed is a lie the
 * operator cannot see through.
 *
 * **The invalidation set carries the revenue report.** Editing a plan moves a
 * figure on the console entry and on the subscription-reporting surface, and a
 * dashboard that keeps showing the old plan name after a rename is drift the
 * operator has no way to notice.
 *
 * **The scope is never submitted.** The owning service writes it from
 * `DEFAULT_ENTITLEMENT_SCOPE` at insert; sending a value here would let a form
 * choose a setting the platform already chose.
 *
 * **Deleting a plan carries no `confirm=true`.** That flag is the wire half of a
 * *hard* removal and the wire declares it on the removals that destroy stored
 * bytes; a plan is retired by a soft delete, and sending the flag where the
 * contract does not declare it would document a guard that does not exist. The
 * confirmation dialog is still required — it is the operator's guard, not the
 * wire's.
 */

export interface PlanWriteBody {
  name: string;
  price: number;
  period_months: number;
}

function usePlanInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: plansKey() });
    void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'revenue') });
  };
}

export function useCreatePlan() {
  const invalidate = usePlanInvalidation();

  return useMutation({
    mutationFn: (body: PlanWriteBody) => api.post<Plan>('finance', '/admin/plans', body),
    onSuccess: invalidate,
    retry: false,
  });
}

export function useUpdatePlan() {
  const invalidate = usePlanInvalidation();

  return useMutation({
    mutationFn: ({ planId, body }: { planId: string; body: PlanWriteBody }) =>
      api.put<Plan>('finance', `/admin/plans/${planId}`, body),
    onSuccess: invalidate,
    retry: false,
  });
}

export function useDeletePlan() {
  const invalidate = usePlanInvalidation();

  return useMutation({
    mutationFn: (planId: string) => api.delete<null>('finance', `/admin/plans/${planId}`),
    onSuccess: invalidate,
    retry: false,
  });
}
